// src/services/aiTools.js
import { pb } from "./pocketbase";
import { cleanSku, getBaseSku, getModelFromSku } from "../utils/skuUtils";
import { formatMoney, getBusinessDaysDiff } from "../utils/formatters";
import { TREINAMENTOS_DATA } from "../data/treinamentosData";
import {
  analisarPrazo,
  getMostCriticalStatus,
  STATUS_PRIORITY,
  formatDiasUteis,
} from "../utils/prazoEngine";
import { getPrazos, getTemposEtapaMap } from "./prazosService";
import { getFeriados } from "./calendarioService";
import { SEED_PRAZOS_MODELO, SEED_TEMPOS_ETAPA } from "../data/prazosSeedData";
import { setFeriadosCache } from "../utils/formatters";

// Helper Anti-ND
const hasVal = (v) => {
  if (!v) return false;
  const s = String(v).trim().toUpperCase();
  return s !== "" && s !== "ND" && s !== "-" && s !== "N/A" && s !== "NULL" && s !== "UNDEFINED";
};

// Helper para normalização de texto sem acentos e minúsculo
const normalizeText = (str) => {
  if (!str) return "";
  return String(str)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
};

// Cache em memória para prazos e matriz de tempos com TTL de 2 minutos
let prazosCache = null;
let prazosCacheTime = 0;

async function loadPrazosContext() {
  const now = Date.now();
  if (prazosCache && now - prazosCacheTime < 2 * 60 * 1000) {
    return prazosCache;
  }

  try {
    const [listPrazos, mapTempos, listFeriados] = await Promise.all([
      getPrazos(),
      getTemposEtapaMap(),
      getFeriados().catch(() => []),
    ]);

    if (Array.isArray(listFeriados) && listFeriados.length > 0) {
      setFeriadosCache(listFeriados);
    }

    const pMap = {};
    if (Array.isArray(listPrazos) && listPrazos.length > 0) {
      listPrazos.forEach((p) => {
        if (p.modelo) pMap[p.modelo] = p;
      });
    } else {
      SEED_PRAZOS_MODELO.forEach((p) => {
        if (p.modelo) pMap[p.modelo] = p;
      });
    }

    let tMap = mapTempos;
    if (!tMap || Object.keys(tMap).length === 0) {
      tMap = {};
      SEED_TEMPOS_ETAPA.forEach((r) => {
        if (!r.modelo || !r.etapa) return;
        if (!tMap[r.modelo]) tMap[r.modelo] = {};
        tMap[r.modelo][r.etapa] = Number(r.dias_ate_conclusao) || 0;
      });
    }

    prazosCache = { prazosMap: pMap, temposEtapaMap: tMap };
    prazosCacheTime = now;
    return prazosCache;
  } catch (err) {
    console.error("[aiTools] Erro ao carregar mapa de prazos:", err);
    const pMap = {};
    SEED_PRAZOS_MODELO.forEach((p) => {
      if (p.modelo) pMap[p.modelo] = p;
    });
    const tMap = {};
    SEED_TEMPOS_ETAPA.forEach((r) => {
      if (!r.modelo || !r.etapa) return;
      if (!tMap[r.modelo]) tMap[r.modelo] = {};
      tMap[r.modelo][r.etapa] = Number(r.dias_ate_conclusao) || 0;
    });
    return { prazosMap: pMap, temposEtapaMap: tMap };
  }
}

export const AI_TOOLS_DEFINITIONS = [
  {
    name: "consultar_manual_treinamento",
    description:
      "Consulta a base oficial de treinamentos, manuais operacionais, POPs, condutas de atendimento e procedimentos industriais da Sempre Joias. Retorna regras de garantia de 60 dias, devoluções/reversas, enxoval de caixas (capelo, diploma, bombom, flanela), montagem, inspeção na lupa e manutenção de máquinas.",
    parameters: {
      type: "object",
      properties: {
        termo_busca: {
          type: "string",
          description:
            "Assunto ou dúvida a consultar (ex: 'garantia 60 dias', 'queda de pedra frete', 'troca de aro', 'enxoval formatura capelo', 'limpeza FEP', 'microporos lupa', 'solda TIG escapulario', 'bomba vacuo oleo', 'drenagem compressor').",
        },
        area: {
          type: "string",
          description: "(Opcional) 'atendimento' para comercial/SAC/expedição ou 'fabrica' para produção/oficina/equipamentos.",
        },
      },
      required: ["termo_busca"],
    },
  },
  {
    name: "consultar_pedido_fabrica",
    description:
      "Consulta detalhada da VISÃO DA FÁBRICA / PRODUÇÃO de um pedido específico. Retorna os itens/peças individuais na linha de produção, a etapa do Kanban (Fundição, Cravação, Polimento, Banho, Pronto), aro/medida, tipo de pedra, metal, gravação, exceções de qualidade e mural de mensagens de produção.",
    parameters: {
      type: "object",
      properties: {
        orderNumber: {
          type: "string",
          description: "O número do pedido (ex: 71740 ou 722361).",
        },
      },
      required: ["orderNumber"],
    },
  },
  {
    name: "consultar_pedido_logistica",
    description:
      "Consulta detalhada da VISÃO LOGÍSTICA / EXPEDIÇÃO de um pedido específico. Retorna o status geral de envio (SEM ETIQUETA, SEPARADO, EMBALADO, POSTADO), destinatário, cidade/UF, código de rastreio dos Correios e status da Nota Fiscal (NF-e).",
    parameters: {
      type: "object",
      properties: {
        orderNumber: {
          type: "string",
          description: "O número do pedido (ex: 71740 ou 722361).",
        },
      },
      required: ["orderNumber"],
    },
  },
  {
    name: "pesquisar_pedidos",
    description:
      "Pesquisa pedidos no banco de dados por critérios objetivos e verificados: nome do cliente, dias úteis em produção (ex: quarto dia útil -> dias_uteis: 4), status logístico ('PRONTO PARA POSTAR', 'SEM ETIQUETA', 'POSTADO'), etapa do Kanban ('Fundição', 'Cravação', 'Polimento', 'Banho') ou pedidos no Arquivo Morto.",
    parameters: {
      type: "object",
      properties: {
        termo_cliente: {
          type: "string",
          description: "Nome ou parte do nome do cliente (ex: 'Renan Caetano' ou 'Amanda').",
        },
        dias_uteis: {
          type: "integer",
          description: "Número exato de dias úteis em produção (ex: 4 para 'quarto dia útil', 1 para 'primeiro dia útil').",
        },
        dias_uteis_min: {
          type: "integer",
          description: "Mínimo de dias úteis em produção (ex: 5 para 'pedidos com 5 ou mais dias úteis').",
        },
        dias_uteis_max: {
          type: "integer",
          description: "Máximo de dias úteis em produção.",
        },
        status_logistico: {
          type: "string",
          description: "Status logístico ('PRONTO PARA POSTAR', 'SEM ETIQUETA', 'SEPARADO', 'POSTADO', 'EMBALADO').",
        },
        etapa_producao: {
          type: "string",
          description: "Etapa de fabricação da peça ('Imprimindo', 'Fundição', 'Cravação', 'Polimento', 'Banho', 'Pronto').",
        },
        termo_sku: {
          type: "string",
          description: "Código SKU específico da joia (ex: 'DIR-TRI' ou 'BIO-ATL-Z-21').",
        },
        apenas_arquivados: {
          type: "boolean",
          description: "Se true, busca exclusivamente no Arquivo Morto / pedidos finalizados e arquivados.",
        },
        incluir_arquivados: {
          type: "boolean",
          description: "Se true, inclui tanto pedidos ativos quanto arquivados.",
        },
        limite: {
          type: "integer",
          description: "Quantidade máxima de pedidos retornados (padrão: 15).",
        },
      },
    },
  },
  {
    name: "consultar_produto",
    description:
      "Consulta os detalhes exatos de um produto no catálogo a partir do seu SKU (ex: DIR-TRI ou BIO-ATL-Z-21). Retorna preço de venda, nome oficial, fotos e especificações.",
    parameters: {
      type: "object",
      properties: {
        sku: {
          type: "string",
          description: "O SKU exato ou baseSku do produto (ex: DIR-TRI, BIO-ATL).",
        },
      },
      required: ["sku"],
    },
  },
  {
    name: "consultar_tarefas",
    description:
      "Consulta a lista de tarefas e prazos do sistema, com filtro opcional por prioridade (urgente, alta, media, baixa) ou status (pendente, concluida).",
    parameters: {
      type: "object",
      properties: {
        priority: {
          type: "string",
          description: "Prioridade opcional: urgente, alta, media, ou baixa.",
        },
        status: {
          type: "string",
          description: "Status da tarefa: pendente ou concluida (padrão: pendente).",
        },
      },
    },
  },
  {
    name: "consultar_previsao_prazos",
    description:
      "Consulta e analisa prazos de produção da fábrica, previsão dinâmica de conclusão e riscos de atraso da oficina. Use para responder com rigor determinístico: 'Quais pedidos estão em risco de atraso?', 'Temos algum atraso hoje?', 'O pedido 71686 vai atrasar?', 'Quantos pedidos vão atrasar?', 'Quais pedidos estão em revisão?', 'Como estão os prazos da oficina?'. Permite consultar um pedido específico ou listar todos os pedidos em risco.",
    parameters: {
      type: "object",
      properties: {
        orderNumber: {
          type: "string",
          description:
            "(Opcional) Número do pedido específico para analisar se vai atrasar, prazo prometido, data prevista e situação de cada peça (ex: '71686' ou '71740'). Se omitido, analisa o panorama de todos os pedidos ativos da fábrica.",
        },
        filtro_risco: {
          type: "string",
          description:
            "(Opcional quando orderNumber for omitido) Filtro de risco: 'todos_em_risco' (vai atrasar, atrasados, apertados e em revisão), 'atrasados' (já extrapolaram o prazo), 'vai_atrasar' (ritmo atual indica atraso provável), 'apertados' (folga crítica <= 1 dia útil), 'em_revisao' (pedidos pausados aguardando análise), ou 'resumo' (apenas contadores estatísticos). Padrão: 'todos_em_risco'.",
        },
        limite: {
          type: "integer",
          description:
            "(Opcional) Quantidade máxima de pedidos retornados na lista geral (padrão: 10, máximo: 25).",
        },
      },
    },
  },
];

export const aiTools = {
  // --- 1. VISÃO DA FÁBRICA / PRODUÇÃO (PEÇAS, AROS, GRAVAÇÃO, MURAL) ---
  async getOrderFactoryDetails(orderNumber, userContext = {}) {
    const cleanNum = String(orderNumber || "").replace(/\D/g, "");
    if (!cleanNum) {
      return { error: "Número do pedido inválido ou não fornecido." };
    }

    try {
      const records = await pb.collection("production_orders").getList(1, 1, {
        filter: `order_number = "${cleanNum}" || order_number ~ "${cleanNum}"`,
      });

      if (!records.items || records.items.length === 0) {
        return {
          encontrado: false,
          mensagem: `Nenhum pedido de produção encontrado com o número #${cleanNum}.`,
        };
      }

      const order = records.items[0];
      const full = order.full_data || {};
      const itemsList = Array.isArray(full.items) ? full.items : [full];

      const pecasProducao = itemsList.map((item, idx) => {
        const itemSpecs = item.specs || full.specs || order.specs || {};
        return {
          item_indice: idx + 1,
          sku: item.sku || order.sku || "N/I",
          nome_produto: item.name || item.product_name || order.product_name || "Joia",
          etapa_kanban: item.production_step || item.status || order.status || "Produção",
          aro_medida: itemSpecs.size || itemSpecs.aro || itemSpecs.tamanho || item.size || "Padrão",
          cor_pedra: itemSpecs.stoneColor || itemSpecs.cor_pedra || itemSpecs.pedra || "Padrão",
          tipo_pedra: itemSpecs.stoneType || itemSpecs.tipo_pedra || "Zircônia",
          metal: itemSpecs.metal || itemSpecs.tipo_metal || "Prata 925",
          gravacao: itemSpecs.engraving || itemSpecs.gravacao || "Sem gravação",
          acabamento: itemSpecs.finish || itemSpecs.acabamento || "Polido",
        };
      });

      // Mural de Recados de Produção
      let muralMensagens = [];
      try {
        const muralRecords = await pb.collection("chat_messages").getFullList({
          filter: `referenced_orders ~ "${cleanNum}" || content ~ "${cleanNum}"`,
          sort: "-created",
          limit: 5,
        });

        muralMensagens = muralRecords.map((m) => ({
          autor: m.sender_name || "Sistema",
          data: m.created ? new Date(m.created).toLocaleDateString("pt-BR") : "-",
          texto: m.content,
        }));
      } catch (_) {}

      const dateObj = order.customCreatedAt || full.convertedAt || order.created;
      const diasUteisProducao = dateObj ? Math.floor(getBusinessDaysDiff(dateObj)) : 0;

      // Análise preditiva do prazo das peças da fábrica
      let analisePrazosResumo = null;
      try {
        const { prazosMap, temposEtapaMap } = await loadPrazosContext();
        const analises = itemsList.map((item) => {
          const sku = item.sku || order.sku || "";
          const modelo = getModelFromSku(sku);
          const prazoRecord = modelo ? prazosMap[modelo] : null;
          const prazoPrometido = prazoRecord?.prazo_prometido;
          const fatorRisco = prazoRecord?.fator_risco || 0;
          const isIntercepted = Boolean(
            item.isInterceptedPE ||
            item.isIntercepted ||
            order.isInterceptedPE ||
            order.isIntercepted
          );
          const statusAtual =
            item.production_step ||
            item.status ||
            order.production_step ||
            order.status ||
            "SOLICITACAO";

          return analisarPrazo({
            dataCompra: dateObj,
            statusAtual,
            prazoPrometido,
            temposEtapa: temposEtapaMap,
            modelo,
            isIntercepted,
            fatorRisco,
          });
        });

        const crit = [...analises].sort(
          (a, b) =>
            (STATUS_PRIORITY[a.status] || 99) - (STATUS_PRIORITY[b.status] || 99)
        )[0];

        if (crit) {
          analisePrazosResumo = {
            status_prazo: crit.status,
            nivel_risco: crit.nivelRisco,
            prazo_prometido_dias_uteis: crit.prazoPrometido,
            data_prometida: crit.dataPrometidaFormatada,
            data_prevista_conclusao: crit.dataPrevistaFormatada,
            tempo_restante_bancada_dias: formatDiasUteis(crit.faltam),
            folga_ou_atraso:
              crit.atrasoEsperado > 0
                ? `Atraso provável de ${formatDiasUteis(crit.atrasoEsperado)} d.ú.`
                : `Folga de ${formatDiasUteis(crit.folga)} d.ú.`,
            diagnostico: crit.descricaoRisco,
          };
        }
      } catch (_) {}

      return {
        encontrado: true,
        numero_pedido: order.order_number || cleanNum,
        cliente: order.customer_name || full.customer_name || "Cliente",
        etapa_geral_fabrica: order.production_step || order.status || "Produção",
        dias_uteis_em_producao: diasUteisProducao,
        prazo_prometido: order.promised_delivery_date || full.promisedDate || "Não informado",
        situacao_prazos: analisePrazosResumo,
        pecas_na_bancada: pecasProducao,
        mural_recados_producao: muralMensagens,
        observacoes_internas: full.observations || order.notes || null,
      };
    } catch (e) {
      console.error("[aiTools] Erro ao consultar fábrica do pedido:", e);
      return { error: `Falha ao buscar dados de produção do pedido #${cleanNum}: ${e.message}` };
    }
  },

  // --- 2. VISÃO LOGÍSTICA / EXPEDIÇÃO (RASTREIO, NF-e, ENDEREÇO) ---
  async getOrderLogisticsDetails(orderNumber, userContext = {}) {
    const cleanNum = String(orderNumber || "").replace(/\D/g, "");
    if (!cleanNum) {
      return { error: "Número do pedido inválido ou não fornecido." };
    }

    try {
      const records = await pb.collection("production_orders").getList(1, 1, {
        filter: `order_number = "${cleanNum}" || order_number ~ "${cleanNum}"`,
      });

      if (!records.items || records.items.length === 0) {
        return {
          encontrado: false,
          mensagem: `Nenhum pedido logístico encontrado com o número #${cleanNum}.`,
        };
      }

      const order = records.items[0];
      const full = order.full_data || {};
      const shipping = full.shipping_address || full.shipping || {};
      const tracking = order.tracking_code || full.tracking_code || full.trackingCode || null;
      const nfe = order.nfe_number || full.nfe_number || full.nfeNumber || null;

      return {
        encontrado: true,
        numero_pedido: order.order_number || cleanNum,
        destinatario: shipping.first_name ? `${shipping.first_name} ${shipping.last_name || ""}`.trim() : order.customer_name || "Cliente",
        status_logistico: order.logistics_status || full.logisticsStatus || order.status || "Pendente",
        transportadora: full.shipping_method || order.carrier || "Correios",
        codigo_rastreio: tracking || "Aguardando postagem / Não gerado",
        link_rastreamento: tracking ? `https://rastreamento.correios.com.br/app/index.php?codigo=${tracking}` : null,
        nota_fiscal: {
          numero_nfe: nfe || "Não emitida",
          chave_acesso: order.nfe_key || full.nfe_key || null,
          status_emissao: nfe ? "Emitida" : "Pendente de emissão",
        },
        cidade_destino: shipping.city ? `${shipping.city}/${shipping.state || ""}` : "Não informada",
        cep: shipping.postcode || shipping.zip || "Não informado",
        data_postagem: order.shipped_at || full.shippedAt || null,
      };
    } catch (e) {
      console.error("[aiTools] Erro ao consultar logística do pedido:", e);
      return { error: `Falha ao buscar dados logísticos do pedido #${cleanNum}: ${e.message}` };
    }
  },

  // --- 3. PESQUISA OBJETIVA DE PEDIDOS ---
  async searchOrders(args = {}, userContext = {}) {
    const {
      termo_cliente,
      dias_uteis,
      dias_uteis_min,
      dias_uteis_max,
      status_logistico,
      etapa_producao,
      termo_sku,
      apenas_arquivados = false,
      incluir_arquivados = false,
      limite = 15,
    } = args;

    try {
      const filters = [];

      if (apenas_arquivados) {
        filters.push("archived = true");
      } else if (!incluir_arquivados) {
        filters.push("archived != true");
      }

      if (termo_cliente?.trim()) {
        const cleanName = termo_cliente.trim().replace(/["']/g, "");
        filters.push(`customer_name ~ "${cleanName}"`);
      }

      if (termo_sku?.trim()) {
        const cleanSkuVal = cleanSku(termo_sku);
        filters.push(`sku ~ "${cleanSkuVal}"`);
      }

      const pbFilter = filters.length > 0 ? filters.join(" && ") : "";

      const records = await pb.collection("production_orders").getFullList({
        filter: pbFilter,
        sort: "-created",
        limit: 200,
      }).catch(() => []);

      const rawItems = Array.isArray(records) ? records : (records.items || []);
      const normLogistics = normalizeText(status_logistico);
      const normStep = normalizeText(etapa_producao);

      const matchedItems = rawItems.filter((r) => {
        const full = r.full_data || {};

        if (apenas_arquivados && !r.archived) return false;
        if (!incluir_arquivados && !apenas_arquivados && r.archived) return false;

        const dateObj = r.customCreatedAt || full.convertedAt || r.created;
        const currentDays = dateObj ? Math.floor(getBusinessDaysDiff(dateObj)) : 0;

        if (dias_uteis !== undefined && dias_uteis !== null) {
          if (currentDays !== Number(dias_uteis)) return false;
        }
        if (dias_uteis_min !== undefined && dias_uteis_min !== null) {
          if (currentDays < Number(dias_uteis_min)) return false;
        }
        if (dias_uteis_max !== undefined && dias_uteis_max !== null) {
          if (currentDays > Number(dias_uteis_max)) return false;
        }

        if (normLogistics && normLogistics !== "fluxo" && normLogistics !== "producao") {
          const currentLogistics = normalizeText(r.logistics_status || full.logisticsStatus || r.status || "");
          if (!currentLogistics.includes(normLogistics)) return false;
        }

        if (normStep && normStep !== "fluxo" && normStep !== "producao") {
          const currentStep = normalizeText(r.production_step || r.status || "");
          if (!currentStep.includes(normStep)) return false;
        }

        return true;
      });

      const maxLimit = Math.min(Number(limite) || 15, 30);
      const sliced = matchedItems.slice(0, maxLimit);

      const formattedResults = sliced.map((o) => {
        const full = o.full_data || {};
        const specs = o.specs || full.specs || {};
        const dateObj = o.customCreatedAt || full.convertedAt || o.created;
        const currentDays = dateObj ? Math.floor(getBusinessDaysDiff(dateObj)) : 0;

        return {
          numero_pedido: o.order_number || full.order?.number || o.id,
          cliente: o.customer_name || full.customer_name || "Cliente",
          sku: o.sku || full.sku || "N/I",
          produto: o.product_name || o.name || full.name || "Joia",
          etapa_fabrica: o.production_step || o.status || "Produção",
          status_logistico: o.logistics_status || full.logisticsStatus || "Pendente",
          dias_uteis: currentDays,
          aro: specs.size || specs.aro || "Padrão",
          pedra: specs.stoneColor || specs.pedra || "Padrão",
          gravacao: specs.engraving || specs.gravacao || "Sem gravação",
        };
      });

      return {
        total_encontrados: matchedItems.length,
        retornados: formattedResults.length,
        pedidos: formattedResults,
      };
    } catch (e) {
      console.error("[aiTools] Erro ao pesquisar pedidos:", e);
      return { error: `Falha ao pesquisar pedidos: ${e.message}` };
    }
  },

  // --- 4. CONSULTA EXATA DE PRODUTO POR SKU ---
  async getProductBySku(sku, catalogMemoryMap = null) {
    const targetSku = cleanSku(sku);
    if (!targetSku) {
      return { error: "SKU inválido ou não informado." };
    }

    if (catalogMemoryMap && catalogMemoryMap[targetSku]) {
      const p = catalogMemoryMap[targetSku];
      return {
        encontrado: true,
        sku: p.sku || targetSku,
        nome: p.name || p.title || "Joia",
        preco: formatMoney(Number(p.price || 0)),
        categoria: p.category || "Geral",
        cor_pedra: p.stoneColor || "",
        modelo: p.model || "",
        status: p.status || "active",
      };
    }

    try {
      const records = await pb.collection("catalog").getList(1, 1, {
        filter: `sku = "${targetSku}" || sku ~ "${targetSku}"`,
      });

      if (records.items && records.items.length > 0) {
        const raw = records.items[0];
        const full = raw.full_data || {};
        return {
          encontrado: true,
          sku: raw.sku || full.sku || targetSku,
          nome: raw.name || full.name || full.title || "Joia",
          preco: formatMoney(Number(full.price || raw.price || 0)),
          categoria: full.category || raw.category || "Joias",
          cor_pedra: full.stoneColor || "",
          modelo: full.model || "",
          status: full.status || "active",
        };
      }

      return {
        encontrado: false,
        mensagem: `Produto com SKU "${targetSku}" não localizado no catálogo.`,
      };
    } catch (e) {
      console.error("[aiTools] Erro ao consultar produto por SKU:", e);
      return { error: `Falha ao buscar produto: ${e.message}` };
    }
  },

  // --- 5. CONSULTA DE TAREFAS DO SISTEMA ---
  async getTasks({ priority, status = "pendente" } = {}) {
    try {
      const filters = [];
      if (status === "pendente") {
        filters.push("completed = false");
      } else if (status === "concluida") {
        filters.push("completed = true");
      }

      if (priority && ["urgente", "alta", "media", "baixa"].includes(priority)) {
        filters.push(`priority = "${priority}"`);
      }

      const pbFilter = filters.length > 0 ? filters.join(" && ") : "";

      const records = await pb.collection("chat_tasks").getFullList({
        filter: pbFilter,
        sort: "-created",
        limit: 20,
      }).catch(() => []);

      return {
        total_tarefas: records.length,
        tarefas: records.map((t) => ({
          id: t.id,
          titulo: t.title,
          prioridade: t.priority || "media",
          status: t.completed ? "Concluída" : "Pendente",
          data_limite: t.due_date || null,
          responsavel: t.assigned_to_name || "Geral",
        })),
      };
    } catch (e) {
      console.error("[aiTools] Erro ao consultar tarefas:", e);
      return { error: `Falha ao buscar tarefas: ${e.message}` };
    }
  },

  // --- 6. CONSULTA DA BASE DE TREINAMENTOS, POPS E CONDUTAS DA EMPRESA ---
  consultarManualTreinamento({ termo_busca, area } = {}) {
    try {
      if (!termo_busca) {
        return { error: "Informe o termo ou dúvida a ser consultada no manual de treinamentos." };
      }

      const cleanQuery = normalizeText(termo_busca);
      const queryWords = cleanQuery.split(/\s+/).filter((w) => w.length > 2);

      const targetAreas = area && TREINAMENTOS_DATA[area]
        ? [TREINAMENTOS_DATA[area]]
        : [TREINAMENTOS_DATA.atendimento, TREINAMENTOS_DATA.fabrica];

      const scoredLessons = [];

      targetAreas.forEach((trilha) => {
        trilha.etapas.forEach((etapa) => {
          etapa.aulas.forEach((aula) => {
            let score = 0;

            const titleNorm = normalizeText(aula.title);
            const codeNorm = normalizeText(aula.code);
            const etapaNorm = normalizeText(etapa.title);
            const popTitleNorm = normalizeText(aula.pop?.title);
            const popTagNorm = normalizeText(aula.pop?.tag);
            const contentNorm = normalizeText(aula.content);
            const keyPointsNorm = (aula.key_points || []).map(normalizeText).join(" ");
            const stepsNorm = (aula.pop?.steps || []).map(normalizeText).join(" ");
            const criticalNorm = normalizeText(aula.pop?.critical_points);
            const expectedNorm = normalizeText(aula.pop?.expected_result);

            // Match da frase completa
            if (contentNorm.includes(cleanQuery) || keyPointsNorm.includes(cleanQuery)) {
              score += 30;
            }

            // Match de palavras-chave
            queryWords.forEach((word) => {
              if (popTagNorm.includes(word)) score += 20;
              if (popTitleNorm.includes(word)) score += 15;
              if (titleNorm.includes(word) || codeNorm.includes(word)) score += 12;
              if (keyPointsNorm.includes(word)) score += 8;
              if (criticalNorm.includes(word)) score += 7;
              if (stepsNorm.includes(word)) score += 5;
              if (contentNorm.includes(word)) score += 3;
              if (etapaNorm.includes(word)) score += 2;
            });

            if (score > 0) {
              scoredLessons.push({
                score,
                trilha: trilha.shortTitle,
                etapa: `Etapa ${etapa.number}: ${etapa.title}`,
                aula_codigo: aula.code,
                aula_titulo: aula.title,
                pontos_chave: aula.key_points || [],
                pop: aula.pop
                  ? {
                      tag: aula.pop.tag,
                      titulo: aula.pop.title,
                      quando_usar: aula.pop.when_to_use,
                      responsavel: aula.pop.responsible,
                      ferramentas: Array.isArray(aula.pop.tools) ? aula.pop.tools.join(", ") : aula.pop.tools,
                      passos: aula.pop.steps,
                      ponto_critico: aula.pop.critical_points,
                      resultado_esperado: aula.pop.expected_result,
                    }
                  : null,
                resumo_procedimento: aula.content ? aula.content.substring(0, 600).trim() + "..." : "",
              });
            }
          });
        });
      });

      scoredLessons.sort((a, b) => b.score - a.score);
      const topResults = scoredLessons.slice(0, 2);

      if (topResults.length === 0) {
        return {
          mensagem: `Nenhum procedimento ou conduta específica encontrada para o termo '${termo_busca}'. Recomende ao colaborador consultar a Central de Treinamentos & POPs no menu Gestão.`,
        };
      }

      return {
        termo_consultado: termo_busca,
        total_encontrados: topResults.length,
        diretrizes_oficiais: topResults.map(({ score, ...rest }) => rest),
      };
    } catch (e) {
      console.error("[aiTools] Erro ao consultar manual de treinamento:", e);
      return { error: `Falha ao buscar manual de treinamento: ${e.message}` };
    }
  },

  // --- 6. PREVISÃO DE PRAZOS E RISCOS DE ATRASO DA OFICINA ---
  async consultarPrevisaoPrazos(args = {}, userContext = {}) {
    const { orderNumber, filtro_risco = "todos_em_risco", limite = 10 } = args;

    try {
      const { prazosMap, temposEtapaMap } = await loadPrazosContext();

      // --- CASO 1: CONSULTA DE UM PEDIDO ESPECÍFICO ---
      if (orderNumber) {
        const cleanNum = String(orderNumber).replace(/\D/g, "");
        const searchNum = cleanNum || String(orderNumber).trim();

        const records = await pb.collection("production_orders").getList(1, 1, {
          filter: `order_number = "${searchNum}" || order_number ~ "${searchNum}"`,
        });

        if (!records.items || records.items.length === 0) {
          return {
            encontrado: false,
            mensagem: `Nenhum pedido de produção encontrado com o número #${searchNum}.`,
          };
        }

        const order = records.items[0];
        const full = order.full_data || {};
        const rawItems =
          Array.isArray(full.items) && full.items.length > 0
            ? full.items
            : Array.isArray(order.items) && order.items.length > 0
            ? order.items
            : [order];

        const dataCompra =
          order.customCreatedAt ||
          order.createdAt ||
          full.convertedAt ||
          full.createdAt ||
          order.created;

        const pecasAnalise = rawItems.map((item, idx) => {
          const sku = item.sku || order.sku || "";
          const modelo = getModelFromSku(sku);
          const prazoRecord = modelo ? prazosMap[modelo] : null;
          const prazoPrometido = prazoRecord?.prazo_prometido;
          const fatorRisco = prazoRecord?.fator_risco || 0;
          const isIntercepted = Boolean(
            item.isInterceptedPE ||
            item.isIntercepted ||
            order.isInterceptedPE ||
            order.isIntercepted
          );
          const statusAtual =
            item.production_step ||
            item.status ||
            order.production_step ||
            order.status ||
            "SOLICITACAO";

          const analise = analisarPrazo({
            dataCompra,
            statusAtual,
            prazoPrometido,
            temposEtapa: temposEtapaMap,
            modelo,
            isIntercepted,
            fatorRisco,
          });

          return {
            item_indice: idx + 1,
            sku,
            modelo: modelo || "Padrão",
            etapa_atual: statusAtual,
            status_prazo: analise.status,
            dias_decorridos: analise.decorrido,
            prazo_prometido_dias: analise.prazoPrometido,
            data_prometida: analise.dataPrometidaFormatada,
            dias_restantes_bancada: formatDiasUteis(analise.faltam),
            data_prevista_conclusao: analise.dataPrevistaFormatada,
            margem_folga_dias: formatDiasUteis(analise.folga),
            atraso_esperado_dias: formatDiasUteis(analise.atrasoEsperado),
            diagnostico: analise.descricaoRisco,
            analise,
          };
        });

        // Identifica a peça mais crítica do pedido
        const itemMaisCritico = [...pecasAnalise].sort(
          (a, b) =>
            (STATUS_PRIORITY[a.analise.status] || 99) -
            (STATUS_PRIORITY[b.analise.status] || 99)
        )[0] || pecasAnalise[0];

        const crit = itemMaisCritico.analise;
        const numPedido = order.order_number || searchNum;

        // Conclusão direta sem rodeios
        let conclusaoDireta = "";
        if (crit.status === "ATRASADO") {
          conclusaoDireta = `SIM, o pedido [${numPedido}] JÁ ESTÁ ATRASADO em ${formatDiasUteis(crit.atrasoEsperado)} dia(s) útil(eis) além da data prometida (${crit.dataPrometidaFormatada}).`;
        } else if (crit.status === "VAI_ATRASAR") {
          conclusaoDireta = `SIM, o pedido [${numPedido}] ESTÁ EM RISCO IMINENTE DE ATRASAR. Previsão dinâmica de conclusão para ${crit.dataPrevistaFormatada} (atraso estimado de ~${formatDiasUteis(crit.atrasoEsperado)} dia(s) útil(eis) além do limite contratual de ${crit.dataPrometidaFormatada}).`;
        } else if (crit.status === "EM_REVISAO") {
          conclusaoDireta = `O pedido [${numPedido}] ESTÁ EM REVISÃO. A produção está pausada aguardando análise para definir se haverá retrabalho ou continuidade.`;
        } else if (crit.status === "APERTADO") {
          conclusaoDireta = `O pedido [${numPedido}] ainda está no prazo, porém com FOLGA CRÍTICA de apenas ${formatDiasUteis(crit.folga)} dia(s) útil(eis). Requer aceleração na oficina para não estourar o prazo.`;
        } else if (crit.status === "PRONTO" || crit.status === "ENVIADO") {
          conclusaoDireta = `NÃO vai atrasar: o pedido [${numPedido}] já está ${crit.status === "PRONTO" ? "PRONTO na oficina (produção concluída sem risco fabril)" : "ENVIADO/EXPEDIDO"}.`;
        } else {
          conclusaoDireta = `NÃO, o pedido [${numPedido}] está FLUINDO DENTRO DO PRAZO. Conclusão prevista para ${crit.dataPrevistaFormatada} com folga confortável de ${formatDiasUteis(crit.folga)} dia(s) útil(eis).`;
        }

        return {
          encontrado: true,
          numero_pedido: `[${numPedido}]`,
          cliente: order.customer_name || full.customer_name || "Cliente",
          conclusao_direta: conclusaoDireta,
          status_geral_prazo: crit.status,
          nivel_risco: crit.nivelRisco,
          dias_decorridos_desde_compra: crit.decorrido,
          prazo_prometido_venda: `${crit.prazoPrometido} dias úteis (Data Limite: ${crit.dataPrometidaFormatada})`,
          previsao_dinamica_conclusao: crit.dataPrevistaFormatada,
          tempo_restante_estimado_oficina: `${formatDiasUteis(crit.faltam)} dia(s) útil(eis)`,
          folga_ou_atraso:
            crit.atrasoEsperado > 0
              ? `Atraso provável de ${formatDiasUteis(crit.atrasoEsperado)} d.ú.`
              : `Folga de ${formatDiasUteis(crit.folga)} d.ú.`,
          peca_mais_critica: {
            sku: itemMaisCritico.sku,
            modelo: itemMaisCritico.modelo,
            etapa_atual: itemMaisCritico.etapa_atual,
            status_prazo: itemMaisCritico.status_prazo,
            diagnostico: itemMaisCritico.diagnostico,
          },
          total_itens: pecasAnalise.length,
          detalhe_todas_as_pecas: pecasAnalise.map(({ analise, ...rest }) => rest),
        };
      }

      // --- CASO 2: PANORAMA GERAL E RISCOS DE ATRASO NA OFICINA ---
      const records = await pb.collection("production_orders").getFullList({
        filter: 'archived != true',
        sort: "+created",
      });

      if (!records || records.length === 0) {
        return {
          mensagem: "Nenhum pedido ativo em produção no momento.",
          kpi_resumo: { total_pedidos_em_producao: 0 },
        };
      }

      // Agrupa itens por pedido
      const orderMap = new Map();

      records.forEach((order) => {
        const full = order.full_data || {};
        const rawItems =
          Array.isArray(full.items) && full.items.length > 0
            ? full.items
            : Array.isArray(order.items) && order.items.length > 0
            ? order.items
            : [order];

        const numPedido = String(
          order.order_number ||
          order.orderNumber ||
          order.order?.number ||
          order.id ||
          "-"
        ).replace(/[[\]#]/g, "").trim();

        const dataCompra =
          order.customCreatedAt ||
          order.createdAt ||
          full.convertedAt ||
          full.createdAt ||
          order.created;

        const customerName =
          order.customer_name ||
          order.customerName ||
          full.customer_name ||
          "Cliente";

        rawItems.forEach((item) => {
          const sku = item.sku || order.sku || "";
          const modelo = getModelFromSku(sku);
          const prazoRecord = modelo ? prazosMap[modelo] : null;
          const prazoPrometido = prazoRecord?.prazo_prometido;
          const fatorRisco = prazoRecord?.fator_risco || 0;
          const isIntercepted = Boolean(
            item.isInterceptedPE ||
            item.isIntercepted ||
            order.isInterceptedPE ||
            order.isIntercepted
          );
          const statusAtual =
            item.production_step ||
            item.status ||
            order.production_step ||
            order.status ||
            "SOLICITACAO";

          const analise = analisarPrazo({
            dataCompra,
            statusAtual,
            prazoPrometido,
            temposEtapa: temposEtapaMap,
            modelo,
            isIntercepted,
            fatorRisco,
          });

          if (!orderMap.has(numPedido)) {
            orderMap.set(numPedido, {
              orderNumber: numPedido,
              customerName,
              dataCompra,
              items: [],
            });
          }

          orderMap.get(numPedido).items.push({
            sku,
            modelo,
            statusAtual,
            analise,
          });
        });
      });

      // Consolidação de KPIs e status mais crítico de cada pedido
      let totalPedidosAtivos = 0;
      let countAtrasados = 0;
      let countVaiAtrasar = 0;
      let countEmRevisao = 0;
      let countApertados = 0;
      let countNoPrazo = 0;
      let countProntos = 0;

      const pedidosProcessados = [];

      for (const group of orderMap.values()) {
        const statuses = group.items.map((it) => it.analise.status);
        const mostCriticalStatus = getMostCriticalStatus(statuses);
        const criticalItem = [...group.items].sort(
          (a, b) =>
            (STATUS_PRIORITY[a.analise.status] || 99) -
            (STATUS_PRIORITY[b.analise.status] || 99)
        )[0] || group.items[0];

        const analise = criticalItem.analise;

        if (analise.status === "CANCELADO") continue;

        totalPedidosAtivos++;

        if (analise.status === "ATRASADO") countAtrasados++;
        else if (analise.status === "VAI_ATRASAR") countVaiAtrasar++;
        else if (analise.status === "EM_REVISAO") countEmRevisao++;
        else if (analise.status === "APERTADO") countApertados++;
        else if (analise.status === "NO_PRAZO") countNoPrazo++;
        else if (analise.status === "PRONTO" || analise.status === "ENVIADO") countProntos++;

        pedidosProcessados.push({
          numero_pedido: `[${group.orderNumber}]`,
          cliente: group.customerName,
          status_prazo: analise.status,
          nivel_risco: analise.nivelRisco,
          peca_critica_sku: criticalItem.sku,
          modelo: criticalItem.modelo || "Padrão",
          etapa_atual: criticalItem.statusAtual,
          dias_decorridos: analise.decorrido,
          prazo_prometido_dias: analise.prazoPrometido,
          data_limite_prometida: analise.dataPrometidaFormatada,
          data_prevista_conclusao: analise.dataPrevistaFormatada,
          dias_restantes_bancada: formatDiasUteis(analise.faltam),
          atraso_esperado_dias: analise.atrasoEsperado,
          folga_restante_dias: analise.folga,
          diagnostico: analise.descricaoRisco,
        });
      }

      // Filtragem por critério de risco
      const normFiltro = String(filtro_risco || "").toLowerCase().trim();
      let pedidosFiltrados = [];

      if (normFiltro === "atrasados") {
        pedidosFiltrados = pedidosProcessados.filter((p) => p.status_prazo === "ATRASADO");
      } else if (normFiltro === "vai_atrasar") {
        pedidosFiltrados = pedidosProcessados.filter((p) => p.status_prazo === "VAI_ATRASAR");
      } else if (normFiltro === "apertados") {
        pedidosFiltrados = pedidosProcessados.filter((p) => p.status_prazo === "APERTADO");
      } else if (normFiltro === "em_revisao") {
        pedidosFiltrados = pedidosProcessados.filter((p) => p.status_prazo === "EM_REVISAO");
      } else if (normFiltro === "resumo") {
        pedidosFiltrados = [];
      } else {
        // "todos_em_risco" (padrão)
        pedidosFiltrados = pedidosProcessados.filter((p) =>
          ["ATRASADO", "VAI_ATRASAR", "EM_REVISAO", "APERTADO"].includes(p.status_prazo)
        );
      }

      // Ordenação por gravidade, atraso ou menor folga
      pedidosFiltrados.sort((a, b) => {
        const prioDiff =
          (STATUS_PRIORITY[a.status_prazo] || 99) -
          (STATUS_PRIORITY[b.status_prazo] || 99);
        if (prioDiff !== 0) return prioDiff;
        if (b.atraso_esperado_dias !== a.atraso_esperado_dias) {
          return b.atraso_esperado_dias - a.atraso_esperado_dias;
        }
        return a.folga_restante_dias - b.folga_restante_dias;
      });

      const maxLimit = Math.min(Math.max(1, Number(limite) || 10), 25);
      const listaExibicao = pedidosFiltrados.slice(0, maxLimit);

      return {
        panorama_geral: {
          total_pedidos_em_producao: totalPedidosAtivos,
          pedidos_atrasados: countAtrasados,
          pedidos_vai_atrasar: countVaiAtrasar,
          pedidos_em_revisao: countEmRevisao,
          pedidos_apertados: countApertados,
          pedidos_no_prazo: countNoPrazo,
          pedidos_prontos_ou_expedidos: countProntos,
          total_em_risco_imediato: countAtrasados + countVaiAtrasar + countApertados,
        },
        filtro_aplicado: normFiltro,
        total_pedidos_no_filtro: pedidosFiltrados.length,
        pedidos_em_destaque: listaExibicao,
        orientacao_geral_oficina:
          countAtrasados > 0 || countVaiAtrasar > 0
            ? `Atenção oficina: temos ${countAtrasados} pedido(s) atrasados e ${countVaiAtrasar} em risco iminente de atraso. Priorize os pedidos listados em destaque na bancada de acabamento/polimento.`
            : countApertados > 0
            ? `Oficina sem atrasos consolidados! Porém há ${countApertados} pedido(s) com folga apertada (<= 1 dia útil). Mantenham o fluxo atento.`
            : "Excelente! Nenhum pedido atrasado ou em risco de atraso na oficina no momento. Todos fluindo no prazo contratual.",
      };
    } catch (e) {
      console.error("[aiTools] Erro ao consultar panorama de prazos:", e);
      return { error: `Falha ao gerar previsão de prazos da oficina: ${e.message}` };
    }
  },

  // --- DESPACHANTE CENTRAL DE FERRAMENTAS ---
  async executeTool(toolName, toolArgs = {}, userContext = {}) {
    switch (toolName) {
      case "consultar_manual_treinamento":
        return this.consultarManualTreinamento(toolArgs);
      case "consultar_pedido_fabrica":
        return this.getOrderFactoryDetails(toolArgs.orderNumber || toolArgs.numero_pedido, userContext);
      case "consultar_pedido_logistica":
        return this.getOrderLogisticsDetails(toolArgs.orderNumber || toolArgs.numero_pedido, userContext);
      case "pesquisar_pedidos":
        return this.searchOrders(toolArgs, userContext);
      case "consultar_produto":
        return this.getProductBySku(toolArgs.sku, userContext.catalogMemoryMap);
      case "consultar_tarefas":
        return this.getTasks(toolArgs);
      case "consultar_previsao_prazos":
        return this.consultarPrevisaoPrazos(toolArgs, userContext);
      default:
        return { error: `Ferramenta desconhecida: ${toolName}` };
    }
  },
};
